import { useState } from 'react';
import { Box, Button, Divider, IconButton, Paper, Typography, Avatar, Chip } from "@mui/material"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import { Phone, LocationOn, Person, HomeWork, Sell, Star, Tag, ThumbUp } from "@mui/icons-material"
import { useCurrency } from "../../context/CurrencyContext"
import Slider from "react-slick"
import BrokerWebButton from "./BrokerWebButton"
import CreateAppointmentModal from "../appointment/AppointmentModal";
import { useAuth } from "../../context/AuthContext";
import { ProfileDto } from "../../types/UserToken";
import Grid from '@mui/material/Grid2';


interface PropertyContactCardProps {
  property: any
  onContactAction: (type: string) => void
  onViewProfile: (id?: number) => void
  cameFromBrokerProfile: boolean
}

const PropertyContactCard = ({
  property,
  onContactAction,
  onViewProfile,
  cameFromBrokerProfile,
}: PropertyContactCardProps) => {
  const { buyRate } = useCurrency()
  const [showPriceInUSD, setShowPriceInUSD] = useState(false)

  // Para Appointments
  const { userToken } = useAuth();
  const currentUser: ProfileDto = {
    id: userToken?.user?.profiles[0]?.id ?? 0,
    username: userToken?.user?.profiles[0]?.username ?? "",
    userProfileId: 0
  };

  const formatPrice = (price: number, propertyCurrency: string) => {
    let locale = "es-CR";
    let symbol = "";

    switch (propertyCurrency) {
      case "CRC":
        locale = "es-CR";
        symbol = "₡";
        break;
      case "USD":
        locale = "en-US";
        symbol = "$";
        break;
      default:
        locale = "en-US";
        symbol = "$";
    }

    return `${symbol}${price.toLocaleString(locale)} ${propertyCurrency}`;
  };

  const convertPrice = (price: number) => {
    const newPrice = buyRate ? price / buyRate : 0
    return `$${newPrice.toLocaleString("en-US")} ${"USD"}`
  }

  const handlePriceToggle = () => {
    setShowPriceInUSD((prevState) => !prevState)
  }

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        mb: 2,
        borderRadius: "16px",
        background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Precio */}
<Box>
  {/* Precio original tachado si hay promoción */}
  {property.promotion && property.promotion.discountPercentage !== 0 && (
    <Typography
      variant="subtitle1"
      sx={{
        mb: 0.5,
        textAlign: "center",
        fontSize: "16px",
        color: "red",
        textDecoration: "line-through",
        textDecorationColor: "red",
      }}
    >
      Precio original: {formatPrice(property.promotion.oldPrice, property.currency)}
    </Typography>
  )}
  
  {/* Precio actual/oferta con tag de descuento */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.4 }}>
    <Typography 
      variant="subtitle1" 
      sx={{ 
        textAlign: "center", 
        fontWeight: "bold", 
        fontSize: "18px",
        color: property.promotion ? "#4caf50" : "inherit"
      }}
    >
      Precio: {formatPrice(property.price, property.currency)}
    </Typography>
    
    {/* Tag de descuento */}
    {property.promotion && property.promotion.discountPercentage !== 0 && (
      <Chip
        label={`-${property.promotion.discountPercentage}% ${property.promotion.title}`}
        size="small"
        sx={{
          backgroundColor: property.promotion.colorTag,
          color: property.promotion.colorText,
          fontWeight: 'bold',
          fontSize: '0.75rem',
        }}
      />
    )}
    
    <IconButton onClick={handlePriceToggle} sx={{ ml: 1 }}>
      <SwapHorizIcon sx={{ color: "#4caf50" }} />
    </IconButton>
  </Box>
  
  {/* Precio convertido */}
  {showPriceInUSD && (
    <Typography 
      variant="subtitle1" 
      sx={{ 
        textAlign: "center", 
        color: "text.secondary", 
        fontWeight: "bold" 
      }}
    >
      Precio en dólares: {convertPrice(property.price)}
    </Typography>
  )}
</Box>

      <Divider />

      {/* Título de contacto */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            mt: 2,
            fontWeight: "bold",
            textAlign: "center",
            position: "relative",
            display: "inline-block",
            left: "50%",
            transform: "translateX(-50%)",
            "&:after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: "10%",
              width: "80%",
              height: "3px",
              background: "linear-gradient(to right, #4caf50, transparent)",
              borderRadius: "2px",
            },
          }}
        >
          {property.realtorTeamName ? "Equipo de Brokers" : "Contacto"}
        </Typography>
      </Box>

      {/* Avatar y Rating */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 4,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Avatar
            alt={property.realtorTeamName ?? property.realtorFullName ?? "Realtor"}
            src={property.realtorTeamName ? "/assets/images/profilePicture.png" : (property.realtorProfileImage ?? "")}
            sx={{
              width: 120,
              height: 120,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              border: "3px solid white",
              cursor: "pointer",
              transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)",
              },
            }}
            onClick={() => property.id_Realtor && onViewProfile(property.id_Realtor)}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -5,
              right: -5,
              backgroundColor: "#4caf50",
              color: "white",
              borderRadius: "12px",
              padding: "2px 8px",
              fontSize: "0.7rem",
              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#2e7d32",
              },
            }}
            onClick={() => property.id_Realtor && onViewProfile(property.id_Realtor)}
          >
            Ver perfil
          </Box>
        </Box>
        {property.realtorTeamName && (
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              fontWeight: "bold",
              textAlign: "center",
              color: "#2e7d32",
            }}
          >
            {property.realtorTeamName}
          </Typography>
        )}
      </Box>

      {/* Información del equipo o agente */}
      {property.realtorTeamName ? (
        <TeamMembersSection property={property} onViewProfile={onViewProfile} />
      ) : (
        <IndividualRealtorSection
          property={property}
          onViewProfile={onViewProfile}
          cameFromBrokerProfile={cameFromBrokerProfile}
        />
      )}

      {/* Botón de acción */}
      <Box sx={{ mt: 8, mb: 2 }}>
        {property.modeDescription === "Alquiler" ? (
          <Button
            variant="contained"
            fullWidth
            onClick={() => onContactAction("Alquiler")}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: "500",
              borderRadius: "12px",
              background: "linear-gradient(45deg, #4caf50, #2e7d32)",
              boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(45deg, #43a047, #2e7d32)",
                boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Alquilar esta propiedad
          </Button>
        ) : property.modeDescription === "Venta" ? (
          <Button
            variant="contained"
            fullWidth
            onClick={() => onContactAction("Compra")}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: "500",
              borderRadius: "12px",
              background: "linear-gradient(45deg, #4caf50, #2e7d32)",
              boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(45deg, #43a047, #2e7d32)",
                boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Comprar esta propiedad
          </Button>

        ) : null}
        {userToken && (
          <CreateAppointmentModal
            clientId={currentUser.id}
            realtorBaseId={property.id_Realtor}
            propertyId={property.id_Property}
            realtorName={property.realtorFullName}
            clientName={currentUser.username}
          />
        )}
      </Box>
    </Paper>
  )
}

interface TeamMembersSectionProps {
  property: any
  onViewProfile: (id?: number) => void
}

const TeamMembersSection = ({ property, onViewProfile }: TeamMembersSectionProps) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 24,
            background: "#4caf50",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Brokers del equipo
        </Typography>
      </Box>

      {property.teamMembers && property.teamMembers.length > 0 && (
        <Box
          sx={{
            mb: 4,
            mx: -2,
            ".slick-dots": {
              bottom: "-25px",
              "& li button:before": {
                color: "#4caf50",
              },
              "& li.slick-active button:before": {
                color: "#2e7d32",
              },
            },
            ".slick-prev, .slick-next": {
              "&:before": {
                color: "#4caf50",
              },
            },
          }}
        >
          <Slider
            dots={true}
            infinite={false}
            speed={2500}
            autoplay={true}
            autoplaySpeed={5000}
            slidesToShow={1}
            slidesToScroll={1}
            arrows={true}
            responsive={[
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  centerMode: false,
                  arrows: false,
                },
              },
            ]}
          >
            {property.teamMembers.map((member: any, index: number) => (
              <Box key={index} sx={{ px: 2, boxSizing: "border-box" }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    background: "rgba(76, 175, 80, 0.05)",
                    border: "1px solid rgba(76, 175, 80, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(76, 175, 80, 0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ position: "relative" }}>
                      <Avatar
                        alt={member.fullName}
                        src={member.profileImage ?? "/assets/images/profilePicture.png"}
                        sx={{
                          width: 60,
                          height: 60,
                          border: "2px solid white",
                          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                          cursor: "pointer",
                          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                        onClick={() => onViewProfile(member.id_Realtor)}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -3,
                          right: -3,
                          backgroundColor: "#4caf50",
                          color: "white",
                          borderRadius: "8px",
                          padding: "1px 5px",
                          fontSize: "0.6rem",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#2e7d32",
                          },
                        }}
                        onClick={() => onViewProfile(member.id_Realtor)}
                      >
                        Ver
                      </Box>
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {member.fullName}
                        {member.isLeader && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              px: 1,
                              py: 0.2,
                              fontSize: "0.7rem",
                              borderRadius: "4px",
                              background: "#4caf50",
                              color: "white",
                            }}
                          >
                            Líder
                          </Box>
                        )}
                      </Typography>
                      {member.rating && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              sx={{
                                fontSize: 16,
                                color: i < Math.floor(member.rating) ? "#FFD700" : "#e0e0e0",
                              }}
                            />
                          ))}
                          <Typography variant="body2" sx={{ ml: 0.5, color: "#757575" }}>
                            ({member.rating})
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: "8px",
                        background: "rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      <Phone sx={{ color: "#4caf50", fontSize: 18, mr: 1.5 }} />
                      <Typography variant="body2">{member.phoneNumber}</Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: "8px",
                        background: "rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      <LocationOn sx={{ color: "#4caf50", fontSize: 18, mr: 1.5 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {member.address}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          p: 1,
                          borderRadius: "8px",
                          background: "rgba(76, 175, 80, 0.08)",
                          flex: 1,
                          mr: 1,
                        }}
                      >
                        <HomeWork sx={{ color: "#4caf50", fontSize: 20 }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                          Alquileres
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {member.rentalCount}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          p: 1,
                          borderRadius: "8px",
                          background: "rgba(76, 175, 80, 0.08)",
                          flex: 1,
                        }}
                      >
                        <Sell sx={{ color: "#4caf50", fontSize: 20 }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                          Ventas
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {member.salesCount}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {member.realtorPageUrl && (
                    <BrokerWebButton
                      key={member.id_Realtor}
                      pageUrl={member.realtorPageUrl}
                      facebookNick={member.facebookNick}
                      instagramNick={member.instagramNick}
                      linkedinNick={member.linkedinNick}
                      xNick={member.xNick}
                      onViewProfile={() => onViewProfile(member.id_Realtor)}
                    />
                  )}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#2e7d32",
                    }}
                  >
                    {property.realtorTeamName}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Slider>
        </Box>
      )}
    </>
  )
}

interface IndividualRealtorSectionProps {
  property: any
  onViewProfile: (id?: number) => void
  cameFromBrokerProfile: boolean
}

const IndividualRealtorSection = ({
  property,
  onViewProfile,
  cameFromBrokerProfile,
}: IndividualRealtorSectionProps) => {
  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        p: 2,
        borderRadius: "12px",
        background: "rgba(76, 175, 80, 0.05)",
        border: "1px solid rgba(76, 175, 80, 0.1)",
      }}
    >
      <Grid container spacing={2}>
        {/* Info básica: nombre, teléfono, dirección */}
        <Grid size={{ xs: 12 }} sx={{ mb: { xs: 2, md: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              borderRadius: "8px",
              background: "white",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              height: "100%",
            }}
          >
            <Person sx={{ color: "#4caf50", fontSize: 20, mr: 1.5 }} />
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              {property.realtorFullName}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mb: { xs: 2, md: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              borderRadius: "8px",
              background: "white",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              height: "100%",
            }}
          >
            <Phone sx={{ color: "#4caf50", fontSize: 20, mr: 1.5 }} />
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              {property.realtorPhone}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mb: { xs: 2, md: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              borderRadius: "8px",
              background: "white",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              height: "100%",
            }}
          >
            <LocationOn sx={{ color: "#4caf50", fontSize: 20, mr: 1.5 }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: "medium",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {property.realtorAddress}
            </Typography>
          </Box>
        </Grid>

        <Grid container width="100%" spacing={2} wrap="wrap" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          {/* Código y Rating */}
          {property.realtorCode && (
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  borderRadius: "8px",
                  background: "white",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  height: "auto",
                  flexDirection: "column",
                }}
              >
                <Tag sx={{ color: "#4caf50", fontSize: 20 }} />
                <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                  Código
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {property.realtorCode}
                </Typography>
              </Box>
            </Grid>
          )}

          {property.realtorRating != null && (
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  borderRadius: "8px",
                  background: "white",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  height: "auto",
                  flexDirection: "column",
                }}
              >
                <ThumbUp sx={{ color: "#4caf50", fontSize: 20 }} />
                <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                  Rating
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold", mr: 0.5 }}>
                    {property.realtorRating}
                  </Typography>
                  <Star sx={{ fontSize: 14, color: "#FFD700" }} />
                </Box>
              </Box>
            </Grid>
          )}

          {/* Alquileres y Ventas */}
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                borderRadius: "8px",
                background: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                height: "auto",
                flexDirection: "column",
              }}
            >
              <HomeWork sx={{ color: "#4caf50", fontSize: 20 }} />
              <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                Alquileres
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {property.realtorRentalCount}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                borderRadius: "8px",
                background: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                height: "auto",
                flexDirection: "column",
              }}
            >
              <Sell sx={{ color: "#4caf50", fontSize: 20 }} />
              <Typography variant="caption" sx={{ mt: 0.5, color: "#757575" }}>
                Ventas
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {property.realtorSalesCount}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Botón para ir a la página web del broker (para broker individual) */}
      {property.realtorPageUrl && (
        <BrokerWebButton
          key={property.id_Realtor}
          pageUrl={property.realtorPageUrl}
          facebookNick={property.realtorFacebookNick}
          instagramNick={property.realtorInstagramNick}
          linkedinNick={property.realtorLinkedinNick}
          xNick={property.realtorXNick}
          onViewProfile={() => onViewProfile(property.id_Realtor)}
        />
      )}
    </Box>
  )
}

export default PropertyContactCard
